import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calculator, Copy, RotateCcw, Info, Shield, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import CVSS31 from '../../utils/cvsscalc31';

const CVSSCalculator = () => {
  const [metrics, setMetrics] = useState({
    AV: '', AC: '', PR: '', UI: '', S: '', C: '', I: '', A: '',
    E: 'X', RL: 'X', RC: 'X',
    CR: 'X', IR: 'X', AR: 'X', MAV: 'X', MAC: 'X', MPR: 'X', MUI: 'X', MS: 'X', MC: 'X', MI: 'X', MA: 'X'
  });
  const [vectorString, setVectorString] = useState('');
  const [results, setResults] = useState(null);

  const metricDefinitions = {
    base: [
      { key: 'AV', name: 'Attack Vector', options: [
        { value: 'N', label: 'Network', desc: 'Remotely exploitable' },
        { value: 'A', label: 'Adjacent', desc: 'Adjacent network access' },
        { value: 'L', label: 'Local', desc: 'Local access required' },
        { value: 'P', label: 'Physical', desc: 'Physical access required' }
      ]},
      { key: 'AC', name: 'Attack Complexity', options: [
        { value: 'L', label: 'Low', desc: 'No special conditions' },
        { value: 'H', label: 'High', desc: 'Requires special conditions' }
      ]},
      { key: 'PR', name: 'Privileges Required', options: [
        { value: 'N', label: 'None', desc: 'No privileges needed' },
        { value: 'L', label: 'Low', desc: 'Basic user privileges' },
        { value: 'H', label: 'High', desc: 'Admin privileges required' }
      ]},
      { key: 'UI', name: 'User Interaction', options: [
        { value: 'N', label: 'None', desc: 'No user action needed' },
        { value: 'R', label: 'Required', desc: 'User action required' }
      ]},
      { key: 'S', name: 'Scope', options: [
        { value: 'U', label: 'Unchanged', desc: 'Same security scope' },
        { value: 'C', label: 'Changed', desc: 'Can affect other components' }
      ]},
      { key: 'C', name: 'Confidentiality', options: [
        { value: 'N', label: 'None', desc: 'No impact' },
        { value: 'L', label: 'Low', desc: 'Some data exposed' },
        { value: 'H', label: 'High', desc: 'All data exposed' }
      ]},
      { key: 'I', name: 'Integrity', options: [
        { value: 'N', label: 'None', desc: 'No impact' },
        { value: 'L', label: 'Low', desc: 'Some data modifiable' },
        { value: 'H', label: 'High', desc: 'All data modifiable' }
      ]},
      { key: 'A', name: 'Availability', options: [
        { value: 'N', label: 'None', desc: 'No impact' },
        { value: 'L', label: 'Low', desc: 'Degraded performance' },
        { value: 'H', label: 'High', desc: 'Total denial of service' }
      ]}
    ],
    temporal: [
      { key: 'E', name: 'Exploit Code Maturity', options: [
        { value: 'X', label: 'Not Defined' },
        { value: 'U', label: 'Unproven' },
        { value: 'P', label: 'Proof-of-Concept' },
        { value: 'F', label: 'Functional' },
        { value: 'H', label: 'High' }
      ]},
      { key: 'RL', name: 'Remediation Level', options: [
        { value: 'X', label: 'Not Defined' },
        { value: 'O', label: 'Official Fix' },
        { value: 'T', label: 'Temporary Fix' },
        { value: 'W', label: 'Workaround' },
        { value: 'U', label: 'Unavailable' }
      ]},
      { key: 'RC', name: 'Report Confidence', options: [
        { value: 'X', label: 'Not Defined' },
        { value: 'U', label: 'Unknown' },
        { value: 'R', label: 'Reasonable' },
        { value: 'C', label: 'Confirmed' }
      ]}
    ]
  };

  useEffect(() => {
    calculateScore();
  }, [metrics]);

  const calculateScore = () => {
    const result = CVSS31.calculateCVSSFromMetrics(
      metrics.AV, metrics.AC, metrics.PR, metrics.UI, metrics.S, metrics.C, metrics.I, metrics.A,
      metrics.E, metrics.RL, metrics.RC, metrics.CR, metrics.IR, metrics.AR,
      metrics.MAV, metrics.MAC, metrics.MPR, metrics.MUI, metrics.MS, metrics.MC, metrics.MI, metrics.MA
    );

    if (result.success) {
      setResults(result);
      setVectorString(result.vectorString);
    } else {
      setResults(null);
      setVectorString('');
    }
  };

  const handleMetricChange = (key, value) => {
    setMetrics(prev => ({ ...prev, [key]: value }));
  };

  const handleVectorInput = (input) => {
    setVectorString(input);
    if (input.startsWith('CVSS:3.1/')) {
      const result = CVSS31.calculateCVSSFromVector(input);
      if (result.success) {
        const parsed = {};
        input.substring(9).split('/').forEach(part => {
          const [key, value] = part.split(':');
          parsed[key] = value;
        });
        setMetrics(prev => ({ ...prev, ...parsed }));
        setResults(result);
      }
    }
  };

  const resetCalculator = () => {
    setMetrics({
      AV: '', AC: '', PR: '', UI: '', S: '', C: '', I: '', A: '',
      E: 'X', RL: 'X', RC: 'X',
      CR: 'X', IR: 'X', AR: 'X', MAV: 'X', MAC: 'X', MPR: 'X', MUI: 'X', MS: 'X', MC: 'X', MI: 'X', MA: 'X'
    });
    setResults(null);
    setVectorString('');
  };

  const copyVector = () => {
    if (vectorString) {
      navigator.clipboard.writeText(vectorString);
      toast.success('Vector string copied to clipboard');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critical': return 'bg-purple-600 text-white';
      case 'High': return 'bg-red-600 text-white';
      case 'Medium': return 'bg-yellow-500 text-black';
      case 'Low': return 'bg-blue-500 text-white';
      case 'None': return 'bg-gray-400 text-white';
      default: return 'bg-gray-200 text-gray-600';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'Critical': return <AlertCircle className="w-5 h-5" />;
      case 'High': return <AlertTriangle className="w-5 h-5" />;
      case 'Medium': return <AlertTriangle className="w-5 h-5" />;
      case 'Low': return <Info className="w-5 h-5" />;
      default: return <CheckCircle className="w-5 h-5" />;
    }
  };

  const MetricSelector = ({ metric, section }) => (
    <div className="space-y-2">
      <label className="text-sm font-medium flex items-center gap-2">
        {metric.name}
        {section === 'base' && <span className="text-red-500">*</span>}
      </label>
      <div className="flex flex-wrap gap-2">
        {metric.options.map(opt => (
          <button
            key={opt.value}
            onClick={() => handleMetricChange(metric.key, opt.value)}
            className={`px-3 py-1.5 text-xs rounded-md border transition-all ${
              metrics[metric.key] === opt.value
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-muted hover:bg-muted/80 border-muted'
            }`}
            title={opt.desc}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h2 className="text-3xl font-bold font-heading mb-2" data-testid="cvss-calculator-title">
          CVSS 3.1 Calculator
        </h2>
        <p className="text-muted-foreground">
          Calculate Common Vulnerability Scoring System (CVSS) scores for security vulnerabilities
        </p>
      </div>

      {/* Vector String Input */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex gap-2">
            <Input
              value={vectorString}
              onChange={(e) => handleVectorInput(e.target.value)}
              placeholder="Enter CVSS vector (e.g., CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H)"
              className="flex-1 font-mono text-sm"
              data-testid="cvss-vector-input"
            />
            <Button variant="outline" onClick={copyVector} disabled={!vectorString}>
              <Copy className="w-4 h-4" />
            </Button>
            <Button variant="outline" onClick={resetCalculator}>
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Score Display */}
        <div className="lg:col-span-1">
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Score Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              {results ? (
                <div className="space-y-6">
                  {/* Base Score */}
                  <div className="text-center p-6 rounded-lg bg-muted">
                    <div className="text-5xl font-bold mb-2">{results.baseMetricScore}</div>
                    <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium ${getSeverityColor(results.baseSeverity)}`}>
                      {getSeverityIcon(results.baseSeverity)}
                      {results.baseSeverity}
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">Base Score</div>
                  </div>

                  {/* Temporal Score */}
                  <div className="flex justify-between items-center p-4 rounded-lg bg-muted/50">
                    <div>
                      <div className="text-sm text-muted-foreground">Temporal</div>
                      <div className="text-2xl font-bold">{results.temporalMetricScore}</div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${getSeverityColor(results.temporalSeverity)}`}>
                      {results.temporalSeverity}
                    </div>
                  </div>

                  {/* Environmental Score */}
                  <div className="flex justify-between items-center p-4 rounded-lg bg-muted/50">
                    <div>
                      <div className="text-sm text-muted-foreground">Environmental</div>
                      <div className="text-2xl font-bold">{results.environmentalMetricScore}</div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${getSeverityColor(results.environmentalSeverity)}`}>
                      {results.environmentalSeverity}
                    </div>
                  </div>

                  {/* Vector String */}
                  <div className="p-3 bg-muted rounded-lg">
                    <div className="text-xs text-muted-foreground mb-1">Vector String</div>
                    <div className="font-mono text-xs break-all">{results.vectorString}</div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Select all base metrics to calculate score</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Metric Selectors */}
        <div className="lg:col-span-2 space-y-6">
          {/* Base Metrics */}
          <Card>
            <CardHeader>
              <CardTitle>Base Metrics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {metricDefinitions.base.map(metric => (
                <MetricSelector key={metric.key} metric={metric} section="base" />
              ))}
            </CardContent>
          </Card>

          {/* Temporal Metrics */}
          <Card>
            <CardHeader>
              <CardTitle>Temporal Metrics (Optional)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {metricDefinitions.temporal.map(metric => (
                <MetricSelector key={metric.key} metric={metric} section="temporal" />
              ))}
            </CardContent>
          </Card>

          {/* Severity Scale Reference */}
          <Card>
            <CardHeader>
              <CardTitle>Severity Scale</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSeverityColor('None')}`}>None (0.0)</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSeverityColor('Low')}`}>Low (0.1-3.9)</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSeverityColor('Medium')}`}>Medium (4.0-6.9)</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSeverityColor('High')}`}>High (7.0-8.9)</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSeverityColor('Critical')}`}>Critical (9.0-10.0)</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CVSSCalculator;
